/** Application detail page — /application/:applicantId (Angular application-details). */

import styles from "../styles/applicationDetail.module.css";
import { ApplicationTabs } from "../components/ApplicationTabs";
import { ApplicationDetailCard } from "../components/ApplicationDetailCard";
import { ApplicationDetailPageState } from "../components/ApplicationDetailPageState";
import { ApplicationDetailOverview } from "../components/ApplicationDetailOverview";
import { ApplicationDetailEditActions } from "../components/ApplicationDetailEditActions";
import { ApplicationDetailModals } from "../components/ApplicationDetailModals";
import { ActionButtons } from "../components/ActionButtons";
import { PageHeader } from "../../invitee/components/PageHeader";
import { useApplicationDetail } from "../hooks/useApplicationDetail";
import {
  hasContractDataForRisk,
  STATUS_MAP,
} from "./applicationDetailHelpers";
import { isDeniedRiskRating } from "../utils/riskRatingAccessFee";

export function ApplicationDetail() {
  const detail = useApplicationDetail();

  const pageStateGuard = ApplicationDetailPageState({
    loading: detail.loading,
    error: detail.error,
    hasApplicationData: Boolean(detail.applicationAllData && detail.applicationData),
  });
  if (pageStateGuard) {
    return (
      <div
        className={styles.applicationDetailsWrap}
        style={{ padding: 0 }}
        data-testid="application-detail-page"
      >
        <PageHeader title="APPLICATION DETAIL" testIdRoot="application-detail-header" />
        <div
          className="px-3 sm:px-6 md:px-8 py-4 sm:py-6"
          data-testid="application-detail-content"
        >
          {pageStateGuard}
        </div>
      </div>
    );
  }

  const { applicationAllData, applicationData, applicantId } = detail;
  if (!applicationAllData || !applicationData || !applicantId) return null;

  const applicantStatusRaw = applicationData.applicant_status as string | undefined;
  const applicantStatusLabel =
    applicantStatusRaw && STATUS_MAP[applicantStatusRaw]
      ? STATUS_MAP[applicantStatusRaw]
      : (applicantStatusRaw ?? "N/A");

  const applicationStatusUpper = String(
    applicationAllData.application_status ?? "",
  ).toUpperCase();
  const showDownloadAccessConfirmation =
    applicationData.progress_status === "Access Sent" &&
    applicationStatusUpper === "APPROVED" &&
    Boolean(applicationAllData.rent_added) &&
    !(
      applicationAllData.balance_due !== null &&
      applicationAllData.balance_due !== undefined &&
      Number(applicationAllData.balance_due) > 0
    ) &&
    !isDeniedRiskRating(applicationAllData.risk_rating);

  const canAdjustRiskRatingCard =
    detail.hasPolicy(["risk_rating_edit"]) &&
    applicationAllData.risk_rating !== null &&
    !["APPROVED", "DENIED", "CANCELLED", "EXPIRED"].includes(applicationStatusUpper);

  const canImpersonate = Boolean(
    applicationData.role_id &&
      detail.hasPolicy(["user_management_impersonate_applicant"]),
  );

  const handleNinjaEquifaxSaved = async () => {
    detail.setScoresInputsHidden(true);
    await detail.refreshApplicationDetail();
  };

  const setSelectedTab = detail.setSelectedTab;

  return (
    <div
      className={styles.applicationDetailsWrap}
      style={{ padding: 0 }}
      data-testid="application-detail-page"
    >
      <PageHeader title="APPLICATION DETAIL" testIdRoot="application-detail-header" />

      <div
        className="px-3 sm:px-6 md:px-8 py-4 sm:py-6"
        data-testid="application-detail-content"
      >
        <ApplicationDetailOverview
          applicantId={applicantId}
          applicationAllData={applicationAllData}
          applicationData={applicationData}
          showChecklist={detail.showChecklist}
          scoresInputsHidden={detail.scoresInputsHidden}
          showUpdateNinjaEquifaxScoresButton={detail.showUpdateNinjaEquifaxScoresButton}
          policyDeclarationAccessLoading={detail.policyDeclarationAccessLoading}
          showDownloadAccessConfirmation={showDownloadAccessConfirmation}
          canImpersonate={canImpersonate}
          canViewChecklist={detail.hasPolicy(["application_detail_credit_report_view"])}
          ninjaScoreTogglePrimaryRef={detail.ninjaScoreTogglePrimaryRef}
          ninjaScoreToggleCoRef={detail.ninjaScoreToggleCoRef}
          ninjaPanelPrimaryRef={detail.ninjaPanelPrimaryRef}
          ninjaPanelCoRef={detail.ninjaPanelCoRef}
          onToggleChecklist={() => detail.setShowChecklist(!detail.showChecklist)}
          onRefresh={() => void detail.refreshApplicationDetail()}
          onCoResidentClick={detail.handleCoResidentClick}
          onImpersonateClick={() => detail.setShowImpersonateModal(true)}
          onNinjaEquifaxToggle={detail.handleNinjaEquifaxScoreToolbarClick}
          onReimbursementExplanation={() => detail.setReimbursementExplanationOpen(true)}
          onDownloadTerms={detail.handleDownloadTerms}
          onNinjaEquifaxSaved={handleNinjaEquifaxSaved}
        />

        <ApplicationDetailCard
          applicationAllData={applicationAllData}
          applicationData={applicationData}
          isCoresident={detail.isCoresident}
          user={detail.user}
          applicantStatusLabel={applicantStatusLabel}
          canCheckInOut={detail.hasPolicy(["application_detail_check_in_out"])}
          canAdjustRiskRating={canAdjustRiskRatingCard}
          onAdjustRiskRating={detail.handleRiskRatingAdjustClick}
          onOpenMaxRentHistory={() => {
            detail.setMaxRentHistoryModalKey((k) => k + 1);
            detail.setShowMaxRentModal(true);
          }}
          onAfterUnderwritingCheck={() => void detail.refreshApplicationDetail()}
          onAfterScreeningIncomeSave={() => void detail.refreshApplicationDetail()}
        />

        <ApplicationTabs
          selectedTab={detail.selectedTab}
          onTabChange={detail.handleTabChange}
          applicationAllData={applicationAllData}
          applicationData={applicationData}
          applicantId={applicantId}
          isEdit={detail.isEdit}
          onFormChange={detail.handleFormChange}
          onRefresh={() => void detail.refreshApplicationDetail()}
          aboutYourselfExposeInlineValidation={detail.aboutYourselfExposeInlineValidation}
          propertyExposeInlineValidation={detail.propertyExposeInlineValidation}
          incomeExposeInlineValidation={detail.incomeExposeInlineValidation}
          hasContractData={hasContractDataForRisk(applicationAllData)}
          onOpenRentChangePopup={() => detail.setPaymentProcessModalOpen(true)}
          showChecklist={detail.showChecklist}
        />

        <ApplicationDetailEditActions
          isEdit={detail.isEdit}
          selectedTab={detail.selectedTab}
          updateButtonRef={detail.updateButtonRef}
          onSave={detail.handleSave}
          onCancel={detail.handleCancelEdit}
        />

        <ApplicationDetailModals
          applicantId={applicantId}
          applicationAllData={applicationAllData}
          applicationData={applicationData}
          selectedTab={detail.selectedTab}
          showImpersonateModal={detail.showImpersonateModal}
          leaseStartDateAdjustConfirmOpen={detail.leaseStartDateAdjustConfirmOpen}
          leaseDetailsChangedConfirmOpen={detail.leaseDetailsChangedConfirmOpen}
          reimbursementApplicationDetailsConfirmOpen={
            detail.reimbursementApplicationDetailsConfirmOpen
          }
          tabChangeSaveConfirm={detail.tabChangeSaveConfirm}
          tabChangeInvalidConfirm={detail.tabChangeInvalidConfirm}
          rentAdjustmentModal={detail.rentAdjustmentModal}
          paymentProcessModalOpen={detail.paymentProcessModalOpen}
          showRiskRatingModal={detail.showRiskRatingModal}
          showMaxRentModal={detail.showMaxRentModal}
          maxRentHistoryModalKey={detail.maxRentHistoryModalKey}
          policyDeclarationModalOpen={detail.policyDeclarationModalOpen}
          policyDeclarationFiles={detail.policyDeclarationFiles}
          reimbursementExplanationOpen={detail.reimbursementExplanationOpen}
          validationModal={detail.validationModal}
          sharedPhoneErrorOpen={detail.sharedPhoneErrorOpen}
          addressErrorOpen={detail.addressErrorOpen}
          noEmploymentModalOpen={detail.noEmploymentModalOpen}
          importPreviousAddressOpen={detail.importPreviousAddressOpen}
          importPreviousEmploymentOpen={detail.importPreviousEmploymentOpen}
          updateButtonRef={detail.updateButtonRef}
          pendingTabAfterSaveRef={detail.pendingTabAfterSaveRef}
          saveModeRef={detail.saveModeRef}
          leaseChangeActionRef={detail.leaseChangeActionRef}
          submitWithoutLeaseDateUpdateRef={detail.submitWithoutLeaseDateUpdateRef}
          noEmploymentConfirmedRef={detail.noEmploymentConfirmedRef}
          setShowImpersonateModal={detail.setShowImpersonateModal}
          setLeaseStartDateAdjustConfirmOpen={detail.setLeaseStartDateAdjustConfirmOpen}
          setLeaseDetailsChangedConfirmOpen={detail.setLeaseDetailsChangedConfirmOpen}
          setReimbursementApplicationDetailsConfirmOpen={
            detail.setReimbursementApplicationDetailsConfirmOpen
          }
          setTabChangeSaveConfirm={detail.setTabChangeSaveConfirm}
          setTabChangeInvalidConfirm={detail.setTabChangeInvalidConfirm}
          setRentAdjustmentModal={detail.setRentAdjustmentModal}
          setPaymentProcessModalOpen={detail.setPaymentProcessModalOpen}
          setShowRiskRatingModal={detail.setShowRiskRatingModal}
          setShowMaxRentModal={detail.setShowMaxRentModal}
          setPolicyDeclarationModalOpen={detail.setPolicyDeclarationModalOpen}
          setReimbursementExplanationOpen={detail.setReimbursementExplanationOpen}
          setValidationModal={detail.setValidationModal}
          setSharedPhoneErrorOpen={detail.setSharedPhoneErrorOpen}
          setAddressErrorOpen={detail.setAddressErrorOpen}
          setNoEmploymentModalOpen={detail.setNoEmploymentModalOpen}
          setImportPreviousAddressOpen={detail.setImportPreviousAddressOpen}
          setAboutYourselfExposeInlineValidation={
            detail.setAboutYourselfExposeInlineValidation
          }
          setPropertyExposeInlineValidation={detail.setPropertyExposeInlineValidation}
          setIncomeExposeInlineValidation={detail.setIncomeExposeInlineValidation}
          setFormData={detail.setFormData}
          setDirtyTabs={detail.setDirtyTabs}
          setSelectedTab={setSelectedTab}
          setUpdateTriggerCounter={detail.setUpdateTriggerCounter}
          performImpersonate={detail.performImpersonate}
          refreshApplicationDetail={detail.refreshApplicationDetail}
          importPreviousAddresses={detail.importPreviousAddresses}
          setImportPreviousDismissed={detail.setImportPreviousDismissed}
          importPreviousEmployment={detail.importPreviousEmployment}
          dismissImportPreviousEmployment={detail.dismissImportPreviousEmployment}
        />

        <ActionButtons
          applicationAllData={applicationAllData}
          applicationData={applicationData}
          applicantId={applicantId}
          selectedTab={detail.selectedTab}
          isCoresident={detail.isCoresident}
          onRefresh={() => {
            void detail.refreshApplicationDetail().catch((err: unknown) => {
              console.error("Failed to refresh:", err);
            });
          }}
          onEditToggle={() => detail.setIsEdit(!detail.isEdit)}
        />
      </div>
    </div>
  );
}
